import {
	resolveAnswerDisplayScore,
	resolveAnswerRawScore,
} from "@memorang/helpers/item";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import type {
	AIFeedbackResponse,
	Answer,
	BaseItemSchema,
} from "@memorang/types";

export type ScoreCriterion = {
	label: string;
	earnedPoints: number;
	maximumScore?: number;
};

export type ScoreContext = {
	earnedPoints: number;
	maximumScore: number;
	criteria: ScoreCriterion[];
};

type AiScoreParams = {
	item: BaseItemSchema;
	aiResponse?: AIFeedbackResponse;
};

export const resolveAiScore = ({ item, aiResponse }: AiScoreParams) =>
	aiResponse?.displayScore ??
	resolveAnswerDisplayScore(item.answer) ??
	aiResponse?.score ??
	resolveAnswerRawScore(item.answer) ??
	item.answer?.score;

export const getPointsEarned = ({
	aiScore,
	correct,
	currentAnswer,
	isAssessorItemType,
	maximumScore,
}: {
	aiScore?: number;
	correct?: boolean;
	currentAnswer?: Answer;
	isAssessorItemType: boolean;
	maximumScore: number;
}) =>
	isAssessorItemType && aiScore != null
		? aiScore
		: (resolveAnswerDisplayScore(currentAnswer) ??
			currentAnswer?.points ??
			(correct ? maximumScore : 0));

export const resolveSubskillAssessments = ({
	item,
	aiResponse,
}: AiScoreParams) =>
	aiResponse?.subskillAssessments ??
	item.answer?.aiResponse?.subskillAssessments ??
	[];

const resolveCriteria = ({
	item,
	aiResponse,
}: AiScoreParams): ScoreCriterion[] => {
	const assessments = resolveSubskillAssessments({ item, aiResponse });
	return assessments.flatMap((assessment) => {
		const earnedPoints = assessment.displayScore ?? assessment.score;
		const label = assessment.name ?? assessment.key;
		if (earnedPoints == null || !label) {
			return [];
		}
		return [
			{
				label,
				earnedPoints,
				maximumScore: assessment.maxScore,
			},
		];
	});
};

type ResolveScoreContextParams = AiScoreParams & {
	answer?: Answer;
	isAssessorItemType: boolean;
};

export const resolveScoreContext = ({
	item,
	answer,
	aiResponse,
	isAssessorItemType,
}: ResolveScoreContextParams): ScoreContext | undefined => {
	const aiScore = resolveAiScore({ item, aiResponse });
	if (!answer && aiScore == null) {
		return undefined;
	}
	const maximumScore = resolveMaxScore(item.scoring) ?? 0;
	return {
		earnedPoints: getPointsEarned({
			aiScore,
			correct: answer?.correct,
			currentAnswer: answer,
			isAssessorItemType,
			maximumScore,
		}),
		maximumScore,
		criteria: resolveCriteria({ item, aiResponse }),
	};
};
